import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Marquee from "react-fast-marquee";
import EmailCapturePopup from "@/components/EmailCapturePopup";
import OGMeta from "@/components/OGMeta";
import {
  MapPin, Trophy, Flame, Store, Share2, Copy, ArrowRight, Crown,
} from "lucide-react";
import MysteryInfo from "@/components/MysteryInfo";

export default function CityLandingPage() {
  const { city } = useParams();
  const [data, setData] = useState(null);
  const location = useLocation();
  const posterFiredKey = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/cities/${encodeURIComponent(city)}`);
        setData(d);
      } catch {
        setData({ live: null, winners: [], sponsors: [], stats: { total_drops: 0, total_winners: 0, total_awarded: 0 }, city });
      }
    })();
  }, [city]);

  // Track QR poster scans once per unique (city + search) combo — survives StrictMode double-effect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("src") !== "poster") return;
    const key = `${city}|${location.search}`;
    if (posterFiredKey.current === key) return;
    posterFiredKey.current = key;
    api.post("/poster-scan", {
      city,
      src: "poster",
      sponsor_id: params.get("sid") || null,
    }).catch(() => {});
  }, [city, location.search]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `Follow the hunt in ${city} → ${shareUrl} #thelocalloot`;

  const copy = async () => {
    await navigator.clipboard.writeText(shareText);
    toast.success("Share link copied");
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `The Local Loot · ${city}`, text: shareText, url: shareUrl }); } catch {}
    } else {
      copy();
    }
  };

  if (!data) {
    return <div className="py-20 text-center text-[#F5F2E8]/60">Loading…</div>;
  }

  const { live, winners, sponsors, stats } = data;

  return (
    <div className="py-8 space-y-10">
      <OGMeta
        title={`${city} · The Local Loot`}
        description={`Cash drops and hunts in ${city}. Follow the clues, find the loot.`}
        image={live ? `${process.env.REACT_APP_BACKEND_URL}/api/og/drop/${live.id}.png` : undefined}
        url={typeof window !== "undefined" ? window.location.href : undefined}
      />
      <EmailCapturePopup city={city} />
      {live && (
        <div
          className="-mx-4 md:-mx-8 border"
          style={{
            borderColor: live.is_golden ? "#D7A62A" : "#E53935",
            background: live.is_golden
              ? "linear-gradient(90deg, rgba(215,166,42,0.18), rgba(15,15,15,0.4), rgba(215,166,42,0.18))"
              : "rgba(229,57,53,0.06)",
          }}
          data-testid="city-live-banner"
        >
          <Marquee gradient={false} speed={45}>
            <span className="font-display text-4xl md:text-6xl uppercase py-3 px-8 tracking-tight">
              <span style={{ color: live.is_golden ? "#D7A62A" : "#E53935" }}>
                {live.is_golden ? "★ GOLDEN DROP LIVE" : "● DROP LIVE"} IN {city.toUpperCase()}
              </span>
              <span className="text-[#F5F2E8]/40"> // </span>
              <span style={{ color: "#F5F2E8" }}>{live.mystery_amount ? "MYSTERY LOOT" : `$${live.amount}`}</span>
              <span className="text-[#F5F2E8]/40"> // </span>
            </span>
          </Marquee>
        </div>
      )}

      <div>
        <div className="overline flex items-center gap-2">
          <MapPin size={12} /> Local hunt page
        </div>
        <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight mt-2 leading-[0.95]">
          {city} <span style={{ color: "#76B82A" }}>hunts.</span>
        </h1>
        <p className="text-[#F5F2E8]/70 mt-4 max-w-xl">
          Real rewards, hidden in real places across {city}. Follow the clues. Make the find. Keep what you win.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/register" className="btn-loot inline-flex items-center gap-2" data-testid="city-signup-cta">
            Follow the hunt <ArrowRight size={16} />
          </Link>
          <button onClick={nativeShare} className="btn-ghost inline-flex items-center gap-2" data-testid="city-share-btn">
            <Share2 size={14} /> Share this page
          </button>
          <button onClick={copy} className="btn-ghost inline-flex items-center gap-2">
            <Copy size={14} /> Copy link
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { k: "Drops", v: stats.total_drops, color: "#F5F2E8" },
          { k: "Winners", v: stats.total_winners, color: "#76B82A" },
          { k: "Awarded", v: `$${stats.total_awarded}`, color: "#D7A62A" },
        ].map((s) => (
          <div key={s.k} className="loot-card-flat p-4">
            <div className="overline">{s.k}</div>
            <div className="font-display text-3xl md:text-5xl mt-1" style={{ color: s.color }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Live drop */}
      {live ? (
        <section>
          <div className="overline mb-3">Hunt live now</div>
          <Link
            to={`/drops/${live.id}`}
            className="loot-card p-6 md:p-8 block relative overflow-hidden"
            style={live.is_golden ? {
              borderColor: "#D7A62A",
              background: "linear-gradient(135deg, rgba(215,166,42,0.10), rgba(15,15,15,1) 60%)",
            } : undefined}
            data-testid="city-live-card"
          >
            {live.is_golden && <Crown size={140} aria-hidden="true" className="absolute -right-6 -top-6 opacity-10" style={{ color: "#D7A62A" }} />}
            <div className="flex items-center gap-3">
              <span className="tag-pill" style={{ color: "#E53935", borderColor: "#E53935" }}>
                <span className="live-dot inline-block mr-2 align-middle" /> LIVE
              </span>
              {live.is_golden && (
                <span className="tag-pill flex items-center gap-1" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>
                  <Crown size={10} /> Golden
                </span>
              )}
            </div>
            <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight mt-3">{live.title}</h3>
            <div className="mt-3 flex items-center gap-3 text-sm text-[#F5F2E8]/70">
              <span className="font-display text-2xl" style={{ color: "#D7A62A" }}>
                {live.mystery_amount ? "Mystery Loot" : `$${live.amount}`}
              </span>
              {live.sponsor && <span>· sponsored by {live.sponsor.business_name}</span>}
            </div>
            <div className="mt-6 inline-flex items-center gap-2 font-mono text-xs text-[#76B82A]">
              Tap to see clues <ArrowRight size={12} />
            </div>
          </Link>
        </section>
      ) : (
        <section className="loot-card-flat p-8 text-center">
          <Flame size={40} className="mx-auto text-[#F5F2E8]/30" />
          <div className="font-display uppercase text-2xl mt-3">No drop live right now</div>
          <p className="text-[#F5F2E8]/60 mt-2">Create an account so you get alerted the moment the next {city} drop goes live.</p>
          <Link to="/register" className="btn-loot inline-block mt-6">Follow the hunt →</Link>
        </section>
      )}

      {/* Winners */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Winners in {city}</div>
          <Link to="/winners" className="font-mono text-xs text-[#76B82A]">All winners →</Link>
        </div>
        {winners.length === 0 ? (
          <div className="loot-card-flat p-8 text-center text-[#F5F2E8]/60">
            No winners yet. Be the first finder.
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-3">
            {winners.map((w) => {
              const pt = w.prize_type || "cash";
              let headline;
              if (w.is_mystery) headline = "🎁 Found Mystery Loot";
              else if (pt === "cash") headline = w.hide_amount ? "Found CASH" : `Found $${w.amount}`;
              else headline = `Found ${w.prize_custom_label || pt.replace(/_/g, " ")}`;
              return (
              <div key={w.id} className="loot-card p-4" data-testid={`city-winner-${w.id}`}>
                <Trophy className="text-[#D7A62A]" size={16} />
                <div className="font-display uppercase text-xl mt-2 leading-tight">@{w.username || w.name}</div>
                <div className="text-sm text-[#F5F2E8]/60 mt-1" style={w.is_mystery ? { color: "#D7A62A" } : undefined}>{headline}</div>
                {w.story && <p className="italic text-xs text-[#F5F2E8]/60 mt-2 line-clamp-3">"{w.story}"</p>}
                {w.sponsor && (
                  <div className="font-mono text-[10px] text-[#F5F2E8]/50 mt-3 uppercase tracking-widest">
                    via {w.sponsor.business_name}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Sponsors */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Local sponsors</div>
          <Link to="/sponsor-apply" className="font-mono text-xs text-[#D7A62A]">Become a sponsor →</Link>
        </div>
        {sponsors.length === 0 ? (
          <div className="loot-card-flat p-6 text-center text-[#F5F2E8]/60">
            No local sponsors yet. Be the first business to fund a {city} hunt.
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-3">
            {sponsors.map((s) => (
              <Link key={s.id} to={`/sponsors/${s.id}`} className="loot-card p-4" data-testid={`city-sponsor-${s.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 border border-[#F5F2E8]/20 flex items-center justify-center" style={{ background: "#123A1A" }}>
                    <Store className="text-[#76B82A]" size={18} />
                  </div>
                  <div>
                    <div className="font-display uppercase text-lg leading-tight">{s.business_name}</div>
                    <div className="font-mono text-[10px] text-[#D7A62A] uppercase tracking-widest mt-1">
                      {s.package.replace("_", " ")}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <section
        className="loot-card-flat p-8 md:p-12 text-center"
        style={{
          background: "linear-gradient(135deg, rgba(118,184,42,0.08), rgba(15,15,15,1) 60%)",
          borderColor: "#76B82A",
        }}
      >
        <div className="overline">Don't miss the next drop</div>
        <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight mt-2">
          Follow the {city} <span style={{ color: "#76B82A" }}>hunt</span>.
        </h2>
        <p className="text-[#F5F2E8]/70 mt-3 max-w-lg mx-auto">
          Stay ready. Pay attention. We alert you the moment clues drop.
        </p>
        <Link to="/register" className="btn-loot inline-block mt-6">Create a hunter account →</Link>
      </section>
    </div>
  );
}
